import { LicenseType } from './authentication/models/licenses';
import { SpacetimeMode } from './spacetime-mode.enum';

/**
 * Restricted Spacetime modes and the license backing each, in tie-break order.
 *
 * The server keeps these mutually exclusive within a scope (LicenseService.Set*LicenseAsync disables
 * the others when one is published), so a tie should only ever come from legacy data. When it does,
 * the first entry wins — declared here rather than left to the order of a chain of ifs.
 */
export const RESTRICTED_SPACETIME_MODES: ReadonlyArray<readonly [LicenseType, SpacetimeMode]> = [
    [LicenseType.SpacetimeSFMCOnly, SpacetimeMode.SFMC],
    [LicenseType.SpacetimeCuracityOnly, SpacetimeMode.Curacity],
    [LicenseType.SpacetimeOttOnly, SpacetimeMode.Ott],
];

/** The restricted mode a single scope's licenses put the user in, or null for none. */
export function restrictedModeForLicenses(licenses: LicenseType[]): SpacetimeMode | null {
    const match = RESTRICTED_SPACETIME_MODES.find(([license]) => licenses.includes(license));
    return match ? match[1] : null;
}

/**
 * A property-level mode overrides a company-level one — the more specific scope wins.
 *
 * A Mixed space concatenates both scopes' licenses, so resolving against the flat union would let a
 * company-wide mode silently mask the mode a CSM set on the property itself.
 *
 * Callers pass only licenses that are active for navigation (see LicenseService.isLicenseVisibleForNav).
 */
export function resolveSpacetimeMode(propertyLicenses: LicenseType[], companyLicenses: LicenseType[]): SpacetimeMode {
    return restrictedModeForLicenses(propertyLicenses)
        ?? restrictedModeForLicenses(companyLicenses)
        ?? SpacetimeMode.Full;
}
